import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/hooks/auth/AuthProvider";
import { useRole } from "@/hooks/useRole";
import { CommunityCategories } from "@/components/mentor/CommunityCategories";
import { fetchGames } from "@/services/gameService";
import { 
  fetchMentorCommunityById, 
  updateCommunity, 
  validateCommunitySlug 
} from "@/services/mentorCommunityService";
import { Game } from "@/types/game";
import { CommunityWithDetails } from "@/types/community";

const EditarComunidade = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const { toast } = useToast();

  console.log('🔧 [EditarComunidade] ID:', id);

  const [community, setCommunity] = useState<CommunityWithDetails | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugChecking, setSlugChecking] = useState(false);

  // Estados para upload de imagem
  const [uploadMode, setUploadMode] = useState<'upload' | 'url'>('url');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    cover_image_url: '',
    rules: '',
    game_id: '',
    is_featured: false,
    is_active: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    console.log('🔧 [EditarComunidade] useEffect disparado:', { 
      id, 
      userId: user?.id, 
      role, 
      roleLoading,
      hasUser: !!user 
    });
    
    // Aguardar carregamento completo do role/user
    if (roleLoading) {
      console.log('🔧 [EditarComunidade] Aguardando carregamento do role...');
      return;
    }

    if (!user) {
      console.log('🔧 [EditarComunidade] Nenhum usuário logado, redirecionando...');
      navigate('/');
      return;
    }

    if (role !== 'mentor') {
      console.log('🔧 [EditarComunidade] Role não é mentor, redirecionando para home. Role atual:', role);
      navigate('/');
      return;
    }

    if (id && user?.id) {
      console.log('🔧 [EditarComunidade] Carregando dados da comunidade...');
      loadData();
    } else {
      console.log('🔧 [EditarComunidade] Parâmetros faltando:', { id, userId: user?.id });
    }
  }, [id, user, role, roleLoading, navigate]);

  const loadData = async () => {
    if (!id || !user?.id) {
      console.log('🔧 [EditarComunidade] loadData - parâmetros faltando:', { id, userId: user?.id });
      return;
    }

    try {
      setLoading(true);
      console.log('🔧 [EditarComunidade] Iniciando carregamento de dados...');
      
      // Buscar dados em paralelo
      const [communityData, gamesData] = await Promise.all([
        fetchMentorCommunityById(id, user.id),
        fetchGames()
      ]);

      console.log('🔧 [EditarComunidade] Dados retornados:', { 
        communityData: !!communityData, 
        gamesCount: gamesData?.length 
      });

      if (!communityData) {
        console.error('🔧 [EditarComunidade] Comunidade não encontrada ou sem permissão');
        toast({
          title: "Erro",
          description: "Comunidade não encontrada ou você não tem permissão para editá-la",
          variant: "destructive",
        });
        navigate('/painel-mentor/comunidades');
        return;
      }

      console.log('✅ [EditarComunidade] Comunidade carregada com sucesso:', communityData.name);
      setCommunity(communityData);
      setGames(gamesData);

      // Preencher formulário
      setFormData({
        name: communityData.name || '',
        slug: communityData.slug || '',
        description: communityData.description || '',
        cover_image_url: communityData.cover_image_url || '',
        rules: communityData.rules || '',
        game_id: communityData.game_id || '',
        is_featured: communityData.is_featured || false,
        is_active: communityData.is_active !== false
      });

      console.log('✅ [EditarComunidade] Formulário preenchido com sucesso');

    } catch (error) {
      console.error('❌ [EditarComunidade] Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Falha ao carregar dados da comunidade",
        variant: "destructive",
      });
      navigate('/painel-mentor/comunidades');
    } finally {
      setLoading(false);
    }
  };

  const generateSlugFromName = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: generateSlugFromName(value)
    }));
  };

  const handleSlugChange = async (value: string) => {
    const cleanSlug = generateSlugFromName(value);
    setFormData(prev => ({ ...prev, slug: cleanSlug }));

    if (cleanSlug && cleanSlug !== community?.slug) {
      setSlugChecking(true);
      try {
        const isAvailable = await validateCommunitySlug(cleanSlug, id);
        if (!isAvailable) {
          setErrors(prev => ({ ...prev, slug: 'Esta URL já está em uso' }));
        } else {
          setErrors(prev => ({ ...prev, slug: '' }));
        }
      } catch (error) {
        console.error('Erro ao validar slug:', error);
      } finally {
        setSlugChecking(false);
      }
    }
  };

  // Funções para upload de imagem
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Erro",
        description: "Por favor, selecione apenas arquivos de imagem.",
        variant: "destructive",
      });
      return;
    }

    // Validar tamanho (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Erro",
        description: "A imagem deve ter no máximo 5MB.",
        variant: "destructive",
      });
      return;
    }

    setImageFile(file);
    
    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (url: string) => {
    setFormData(prev => ({ ...prev, cover_image_url: url }));
    setImagePreview(url);
  };

  const removeImage = () => {
    setImageFile(null);
    setFormData(prev => ({ ...prev, cover_image_url: '' }));
    setImagePreview('');
  };

  const uploadImageToSupabase = async (file: File): Promise<string> => {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Gerar nome único para o arquivo
    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(2);
    const fileExtension = file.name.split('.').pop();
    const fileName = `community-covers/${timestamp}-${randomId}.${fileExtension}`;

    try {
      console.log('📤 [uploadImageToSupabase] Tentando upload para bucket games...', fileName);

      // Tentar usar o bucket 'games' que já existe e tem permissões
      const { data, error } = await supabase.storage
        .from('games')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('❌ [uploadImageToSupabase] Erro no upload para games:', error);
        
        // Tentar bucket alternativo 'profile-images'
        console.log('📤 [uploadImageToSupabase] Tentando bucket profile-images...');
        const { data: data2, error: error2 } = await supabase.storage
          .from('profile-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error2) {
          console.error('❌ [uploadImageToSupabase] Erro no upload para profile-images:', error2);
          throw new Error('Falha no upload para ambos os buckets');
        }

        // Obter URL pública do bucket profile-images
        const { data: urlData2 } = supabase.storage
          .from('profile-images')
          .getPublicUrl(fileName);

        console.log('✅ [uploadImageToSupabase] Upload realizado com sucesso no profile-images');
        return urlData2.publicUrl;
      }

      // Obter URL pública do bucket games
      const { data: urlData } = supabase.storage
        .from('games')
        .getPublicUrl(fileName);

      console.log('✅ [uploadImageToSupabase] Upload realizado com sucesso no games');
      return urlData.publicUrl;

    } catch (error) {
      console.error('❌ [uploadImageToSupabase] Erro geral no upload:', error);
      throw new Error('Falha ao fazer upload da imagem');
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'URL é obrigatória';
    }

    if (!formData.game_id) {
      newErrors.game_id = 'Jogo é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !id || !user?.id) return;

    try {
      setSaving(true);

      let coverImageUrl = formData.cover_image_url;
      
      // Se há imagem para upload
      if (uploadMode === 'upload' && imageFile) {
        try {
          coverImageUrl = await uploadImageToSupabase(imageFile);
        } catch (uploadError: any) {
          console.error('❌ [EditarComunidade] Erro no upload:', uploadError);
          toast({
            title: "Erro no Upload",
            description: uploadError.message || "Falha ao fazer upload da imagem. Tente usar uma URL externa.",
            variant: "destructive",
          });
          return;
        }
      }

      // Debug: vamos ver exatamente o que está sendo enviado
      // Garantir que strings vazias sejam convertidas para null para evitar constraint violations
      // Função helper para normalizar strings
      const normalizeString = (value: string | undefined | null): string | null => {
        if (value === undefined || value === null) return null;
        const trimmed = value.trim();
        return trimmed === '' ? null : trimmed;
      };

      const updateData = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        description: normalizeString(formData.description),
        cover_image_url: normalizeString(coverImageUrl),
        rules: normalizeString(formData.rules),
        game_id: formData.game_id,
        is_featured: formData.is_featured,
        is_active: formData.is_active
      };

      console.log('🔍 [EditarComunidade] Dados que serão enviados:', updateData);
      console.log('🔍 [EditarComunidade] Descrição DEBUG:', {
        original: formData.description,
        trimmed: formData.description?.trim(),
        final: updateData.description,
        type: typeof updateData.description,
        length: formData.description?.trim()?.length || 0,
        isEmpty: formData.description?.trim() === '',
        isNull: updateData.description === null
      });
      
      // Log adicional para todas as strings que podem causar constraint violation
      console.log('🔍 [EditarComunidade] Validação de constraints:', {
        description: {
          value: updateData.description,
          type: typeof updateData.description,
          isNull: updateData.description === null,
          length: updateData.description?.length || 0,
          isValidForDb: updateData.description === null || (typeof updateData.description === 'string' && updateData.description.length > 0 && updateData.description.length <= 500)
        },
        cover_image_url: {
          value: updateData.cover_image_url,
          type: typeof updateData.cover_image_url,
          isNull: updateData.cover_image_url === null
        },
        rules: {
          value: updateData.rules,
          type: typeof updateData.rules,
          isNull: updateData.rules === null
        }
      });

      // Validação específica para a constraint de descrição
      if (updateData.description !== null && 
          (typeof updateData.description !== 'string' || 
           updateData.description.length === 0 || 
           updateData.description.length > 500)) {
        console.error('❌ [EditarComunidade] Descrição inválida para constraint:', updateData.description);
        toast({
          title: "Erro de Validação",
          description: "A descrição deve estar vazia ou ter entre 1 e 500 caracteres.",
          variant: "destructive",
        });
        return;
      }

      await updateCommunity(id, user.id, updateData);

      toast({
        title: "Sucesso",
        description: "Comunidade atualizada com sucesso!",
      });

      navigate('/painel-mentor/comunidades');

    } catch (error: any) {
      console.error('Erro ao atualizar comunidade:', error);
      toast({
        title: "Erro",
        description: error.message || "Falha ao atualizar comunidade",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/painel-mentor/comunidades')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white">
                Editar Comunidade
              </h1>
              <p className="text-gray-400">
                {community?.name}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="max-w-2xl">
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Informações da Comunidade</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white">
                  Nome da Comunidade *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Digite o nome da comunidade"
                  className="bg-gray-700 border-gray-600 text-white"
                />
                {errors.name && (
                  <p className="text-red-400 text-sm">{errors.name}</p>
                )}
              </div>

              {/* Slug */}
              <div className="space-y-2">
                <Label htmlFor="slug" className="text-white">
                  URL da Comunidade *
                </Label>
                <div className="flex items-center">
                  <span className="text-gray-400 text-sm mr-2">
                    /comunidades/
                  </span>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleSlugChange(e.target.value)}
                    placeholder="url-da-comunidade"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                  {slugChecking && (
                    <div className="ml-2">
                      <LoadingSpinner size="sm" />
                    </div>
                  )}
                </div>
                {errors.slug && (
                  <p className="text-red-400 text-sm">{errors.slug}</p>
                )}
                <p className="text-gray-400 text-sm">
                  Esta será a URL da sua comunidade. Use apenas letras, números e hífens.
                </p>
              </div>

              {/* Jogo */}
              <div className="space-y-2">
                <Label htmlFor="game" className="text-white">
                  Jogo *
                </Label>
                <Select
                  value={formData.game_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, game_id: value }))}
                >
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Selecione um jogo" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {games.map((game) => (
                      <SelectItem key={game.id} value={game.id} className="text-white">
                        {game.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.game_id && (
                  <p className="text-red-400 text-sm">{errors.game_id}</p>
                )}
              </div>

              {/* Descrição */}
              <div className="space-y-2">
                <Label htmlFor="description" className="text-white">
                  Descrição
                  <span className="text-gray-400 text-sm ml-2">
                    ({formData.description.length}/500)
                  </span>
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 500) {
                      setFormData(prev => ({ ...prev, description: value }));
                    }
                  }}
                  placeholder="Descreva sua comunidade... (máximo 500 caracteres)"
                  rows={4}
                  className={`bg-gray-700 border-gray-600 text-white ${
                    formData.description.length > 450 ? 'border-yellow-500' : ''
                  } ${formData.description.length >= 500 ? 'border-red-500' : ''}`}
                />
                {formData.description.length > 450 && (
                  <p className={`text-sm ${
                    formData.description.length >= 500 ? 'text-red-400' : 'text-yellow-400'
                  }`}>
                    {formData.description.length >= 500 
                      ? 'Limite de caracteres atingido!'
                      : `Você está próximo do limite (${500 - formData.description.length} caracteres restantes)`
                    }
                  </p>
                )}
              </div>

              {/* Imagem de Capa */}
              <div className="space-y-2">
                <Label htmlFor="cover_image_url" className="text-white">
                  Imagem de Capa
                </Label>
                
                {/* Instruções detalhadas */}
                <div className="bg-gray-700 border border-gray-600 rounded-lg p-4 mb-3">
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-bold">i</span>
                    </div>
                    <div>
                      <h4 className="text-blue-400 font-semibold text-sm mb-2">Instruções para Imagem de Capa</h4>
                      <ul className="text-gray-300 text-sm space-y-1">
                        <li className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                          <span><strong>Dimensões recomendadas:</strong> 1200x400 pixels (proporção 3:1)</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                          <span><strong>Formatos aceitos:</strong> JPG, PNG, WebP</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                          <span><strong>Tamanho máximo:</strong> 5MB</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                          <span><strong>Dica:</strong> Use imagens com boa qualidade e que representem bem sua comunidade</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1 h-1 bg-blue-400 rounded-full"></span>
                          <span><strong>Evite:</strong> Imagens com muito texto ou elementos pequenos que ficam ilegíveis</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Seletor de modo de upload */}
                <div className="flex gap-2 mb-4">
                  <Button
                    type="button"
                    variant={uploadMode === 'upload' ? 'default' : 'outline'}
                    onClick={() => setUploadMode('upload')}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload de Arquivo
                  </Button>
                  <Button
                    type="button"
                    variant={uploadMode === 'url' ? 'default' : 'outline'}
                    onClick={() => setUploadMode('url')}
                    className="flex-1"
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    URL Externa
                  </Button>
                </div>

                {/* Upload de arquivo */}
                {uploadMode === 'upload' && (
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-gray-600 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-gray-400 text-sm">
                          Clique para selecionar uma imagem
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          JPG, PNG ou WebP até 5MB
                        </p>
                      </label>
                    </div>
                    
                    {imageFile && (
                      <div className="flex items-center justify-between bg-gray-700 p-3 rounded">
                        <span className="text-white text-sm">{imageFile.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={removeImage}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* URL externa */}
                {uploadMode === 'url' && (
                  <Input
                    id="cover_image_url"
                    value={formData.cover_image_url}
                    onChange={(e) => handleImageUrlChange(e.target.value)}
                    placeholder="https://exemplo.com/imagem-capa-comunidade.jpg"
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                )}

                {/* Preview da imagem */}
                {(imagePreview || formData.cover_image_url) && (
                  <div className="mt-3">
                    <Label className="text-white text-sm mb-2 block">Preview:</Label>
                    <div className="relative w-full h-32 bg-gray-700 rounded-lg overflow-hidden">
                      <img
                        src={imagePreview || formData.cover_image_url}
                        alt="Preview da capa"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "https://via.placeholder.com/400x200/374151/9CA3AF?text=Erro+ao+carregar+imagem";
                        }}
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={removeImage}
                        className="absolute top-2 right-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Regras */}
              <div className="space-y-2">
                <Label htmlFor="rules" className="text-white">
                  Regras da Comunidade
                </Label>
                <Textarea
                  id="rules"
                  value={formData.rules}
                  onChange={(e) => setFormData(prev => ({ ...prev, rules: e.target.value }))}
                  placeholder="Defina as regras da sua comunidade..."
                  rows={4}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>

              {/* Switches */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Comunidade Ativa</Label>
                    <p className="text-gray-400 text-sm">
                      Comunidades inativas não aparecem nas buscas
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-white">Comunidade em Destaque</Label>
                    <p className="text-gray-400 text-sm">
                      Comunidades em destaque aparecem no topo das buscas
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))}
                  />
                </div>
              </div>

              {/* Botões */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="submit"
                  disabled={saving || slugChecking || !!errors.slug}
                  className="bg-blue-600 hover:bg-blue-700 flex-1"
                >
                  {saving ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar Alterações
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/painel-mentor/comunidades')}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Seção de Categorias de Posts */}
        <div className="max-w-2xl mt-8">
          <CommunityCategories communityId={id!} />
        </div>
      </div>
    </div>
  );
};

export default EditarComunidade; 